import React, { useState, useEffect } from 'react';
import { UserPlus, Plus, Trash2, Edit2, X, Check, Phone, Mail, User, CheckCircle, XCircle, Search } from 'lucide-react';
import apiService from '../services/api';

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 4000;
    const interval = 10;
    const decrement = (100 * interval) / duration;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return prev - decrement;
      });
    }, interval);

    const closeTimer = setTimeout(() => onClose(), duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  const config = {
    success: {
      bgColor: 'bg-white border border-green-200 shadow-soft',
      progressColor: 'bg-green-500',
      textColor: 'text-green-800'
    },
    error: {
      bgColor: 'bg-white border border-red-200 shadow-soft',
      progressColor: 'bg-red-500',
      textColor: 'text-red-800'
    }
  };

  const currentConfig = config[type] || config.success;

  return (
    <div className={`${currentConfig.bgColor} ${currentConfig.textColor} rounded-xl overflow-hidden min-w-[320px] max-w-md animate-slideIn`}>
      <div className="px-5 py-4 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-relaxed break-words">{message}</p>
        </div>
        <button onClick={onClose} className="flex-shrink-0 hover:bg-ink-100 rounded-lg p-1.5 transition-all duration-200 ml-2">
          <X size={18} />
        </button>
      </div>
      <div className="h-1 bg-ink-100">
        <div className={`h-full ${currentConfig.progressColor} transition-all duration-100 ease-linear`} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
const ConfirmDialog = ({ isOpen, clientName, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lifted max-w-md w-full animate-scale-in">
        <div className="px-6 py-4 border-b border-ink-200">
          <h3 className="text-lg font-semibold text-ink-900">¿Eliminar cliente?</h3>
        </div>
        <div className="px-6 py-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Cliente:</strong> {clientName}
            </p>
            <p className="text-xs text-yellow-700 mt-2">
              Este cliente dejará de aparecer en el sistema, pero podrás recuperarlo desde Administración.
            </p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Eliminar Cliente
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
function AltaCliente() {
  const [clientes, setClientes] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingCliente, setEditingCliente] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    nombre: '',
    contacto: '',
    telefono: '',
    correo: ''
  });

  useEffect(() => {
    fetchClientes();
  }, []);

  // ── Fetches ──────────────────────────────────────────────────────────────
  const fetchClientes = async () => {
    try {
      const data = await apiService.get('/clientes');
      setClientes(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast('Error al cargar clientes', 'error');
      }
    }
  };

  // ── Toasts ───────────────────────────────────────────────────────────────
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // ── Crear / Editar cliente ───────────────────────────────────────────────
  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (modalMode === 'edit') {
        await apiService.put(`/clientes/${editingCliente.id}`, formData);
        showToast(`Cliente "${formData.nombre}" actualizado exitosamente`, 'success');
      } else {
        await apiService.post('/clientes', formData);
        showToast(`Cliente "${formData.nombre}" registrado exitosamente`, 'success');
      }
      setFormData({ nombre: '', contacto: '', telefono: '', correo: '' });
      setEditingCliente(null);
      setShowModal(false);
      await fetchClientes();
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast(error.message || 'Error al guardar cliente', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Eliminar cliente ─────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    try {
      await apiService.delete(`/clientes/${encodeURIComponent(clienteToDelete.nombre)}`);
      showToast(`Cliente "${clienteToDelete.nombre}" eliminado`, 'success');
      await fetchClientes();
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast(error.message || 'Error al eliminar cliente', 'error');
      }
    }
    setShowConfirmDialog(false);
    setClienteToDelete(null);
  };

  // ── Filtrar clientes ─────────────────────────────────────────────────────
  const filteredClientes = clientes.filter((cliente) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      cliente.nombre?.toLowerCase().includes(term) ||
      cliente.contacto?.toLowerCase().includes(term) ||
      cliente.telefono?.toLowerCase().includes(term) ||
      cliente.correo?.toLowerCase().includes(term)
    );
  });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-3">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
        ))}
      </div>

      {/* Confirm dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        clientName={clienteToDelete?.nombre}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setClienteToDelete(null);
        }}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-8 animate-fade-in">
        <div>
          <h1 className="font-display text-3xl text-ink-900 mb-1">Alta Cliente</h1>
          <p className="text-ink-400">Registra y gestiona los clientes del sistema</p>
        </div>
        <button
          onClick={() => {
            setFormData({ nombre: '', contacto: '', telefono: '', correo: '' });
            setEditingCliente(null);
            setModalMode('create');
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition shadow-soft"
        >
          <Plus size={20} />
          <span>Nuevo Cliente</span>
        </button>
      </div>

      {/* Buscador */}
      <div className="mb-6 animate-fade-in">
        <div className="relative max-w-2xl">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search size={20} className="text-ink-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, contacto, teléfono o correo..."
            className="w-full pl-12 pr-4 py-3 bg-white border border-ink-200 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-ink-900 focus:outline-none transition shadow-soft"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-ink-400 hover:text-ink-700 transition"
            >
              <X size={20} />
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="mt-2 text-sm text-ink-500">
            {filteredClientes.length} {filteredClientes.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}
          </p>
        )}
      </div>

      {/* Card Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredClientes.map((cliente) => (
          <div
            key={cliente.id}
            className="bg-white rounded-xl shadow-soft border border-ink-200 overflow-hidden hover:shadow-lifted transition-all duration-300 group"
          >
            {/* Nombre */}
            <div className="px-5 py-4 bg-white border-l-4 border-ink-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-700 font-bold text-lg">
                      {cliente.nombre?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-ink-900">{cliente.nombre}</h3>
                    <p className="text-xs text-ink-400">
                      {cliente.totalContenedores > 0
                        ? `${cliente.totalContenedores} contenedor${cliente.totalContenedores !== 1 ? 'es' : ''}`
                        : 'Sin contenedores aún'}
                    </p>
                  </div>
                </div>

                {/* Botones editar / eliminar — aparecen en hover */}
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => {
                      setFormData({
                        nombre: cliente.nombre || '',
                        contacto: cliente.contacto || '',
                        telefono: cliente.telefono || '',
                        correo: cliente.correo || ''
                      });
                      setEditingCliente(cliente);
                      setModalMode('edit');
                      setShowModal(true);
                    }}
                    className="p-2 text-ink-400 hover:text-ink-700 hover:bg-ink-100 rounded-lg transition"
                    title="Editar cliente"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => {
                      setClienteToDelete(cliente);
                      setShowConfirmDialog(true);
                    }}
                    className="p-2 text-ink-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Eliminar cliente"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Info de contacto */}
            <div className="px-5 py-4 bg-ink-50">
              {cliente.contacto || cliente.telefono || cliente.correo ? (
                <div className="space-y-2">
                  {cliente.contacto && (
                    <div className="flex items-center gap-2">
                      <User size={15} className="text-ink-400 flex-shrink-0" />
                      <span className="text-sm text-ink-700">{cliente.contacto}</span>
                    </div>
                  )}
                  {cliente.telefono && (
                    <div className="flex items-center gap-2">
                      <Phone size={15} className="text-ink-400 flex-shrink-0" />
                      <span className="text-sm text-ink-700">{cliente.telefono}</span>
                    </div>
                  )}
                  {cliente.correo && (
                    <div className="flex items-center gap-2">
                      <Mail size={15} className="text-ink-400 flex-shrink-0" />
                      <span className="text-sm text-ink-700">{cliente.correo}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-ink-400 italic">Sin información de contacto</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {filteredClientes.length === 0 && (
        <div className="text-center py-16 animate-fade-in">
          {clientes.length === 0 ? (
            <>
              <UserPlus size={64} className="mx-auto text-ink-300 mb-4" />
              <h3 className="text-xl font-semibold text-ink-900 mb-2">No hay clientes registrados</h3>
              <p className="text-ink-600">Crea tu primer cliente con el botón "Nuevo Cliente"</p>
            </>
          ) : (
            <>
              <Search size={64} className="mx-auto text-ink-300 mb-4" />
              <h3 className="text-xl font-semibold text-ink-900 mb-2">No se encontraron clientes</h3>
              <p className="text-ink-600">Intenta con otro término de búsqueda</p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition"
              >
                Limpiar búsqueda
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Modal crear cliente ──────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lifted w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header modal */}
            <div className="px-6 py-4 border-b border-ink-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-ink-900">{modalMode === 'edit' ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
              <button onClick={() => setShowModal(false)} className="text-ink-400 hover:text-ink-600">
                <X size={24} />
              </button>
            </div>

            {/* Campos */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la empresa *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Ej. Grupo Logística SA"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del contacto</label>
                <input
                  type="text"
                  value={formData.contacto}
                  onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Ej. Juan Pérez"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Ej. 55 1234 5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
                <input
                  type="email"
                  value={formData.correo}
                  onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Ej. contacto@empresa.com"
                />
              </div>
            </div>

            {/* Footer modal */}
            <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={isSubmitting || !formData.nombre.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check size={20} />
                <span>{isSubmitting ? 'Guardando...' : (modalMode === 'edit' ? 'Guardar Cambios' : 'Guardar Cliente')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AltaCliente;
